import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";

import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";

import {
  AccountCircleOutlined,
  CheckCircleOutline,
  StorefrontOutlined,
} from "@mui/icons-material";
import FancyRadioButton from "app/components/Form/FancyRadioButton";
import ImageUpload from "app/components/Form/ImageUpload";
import { states } from "data/states";
import { validateEmail, validateName, validatePassword } from "util/validation";

import { useSignUpMutation } from "lib/features/auth/authService";
import {
  resetAuthState,
  selectActiveStep,
  selectSignUpForm,
  setActiveStep,
  setSignUpFormState,
} from "lib/features/auth/authSlice";
import {
  CreateBusiness,
  RoleId,
  SignUpFormState,
} from "lib/features/auth/types";
import { useGetInfluencerTypeQuery } from "lib/features/influencer/influencerService";
import { useAppDispatch, useAppSelector } from "lib/hooks";
import { roles } from "./data";

import { Dispatch } from "@reduxjs/toolkit";
import { useGetIndustryTypesQuery } from "lib/features/industries/industriesService";

import { isFetchBaseQueryError } from "util/errorValidation";

import { Industry } from "interfaces/busines-item-response";
import { awsImageRootUrl } from "lib/constants";
import { useCreateBusinessMutation } from "lib/features/business/businessService";
import { Category, fetchCategory } from "lib/features/category/categoryService";
import { InfluencerType } from "lib/features/influencer/types";
import { useGetRegionsQuery } from "lib/features/region/regionService";
import { setUser } from "lib/features/user/userSlice";
import { useNavigate } from "react-router-dom";
import authServices, {
  CreateUserDto,
  UserCustomerType,
} from "services/auth-service";

import { User } from "lib/features/user/types";
import EyeClose from "../../../assets/icons/eye-close.svg";
import EyeIcon from "../../../assets/icons/eye.svg";

const SignUpForm: React.FC = () => {
  const dispatch: Dispatch = useAppDispatch();
  const navigate = useNavigate();
  const formData = useAppSelector(selectSignUpForm);
  const activeStep = useAppSelector(selectActiveStep);
  const [emailError, setEmailError] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState<number>();
  const [businessTypesData, setBusinessTypesData] = useState<Category[]>([]);
  const [customerTypesList, setCustomerTypeList] = useState<UserCustomerType[]>(
    []
  );
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<
    Record<string, string | null>
  >({});
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const setFormData = (data: SignUpFormState) =>
    dispatch(setSignUpFormState(data));

  const [
    signUp,
    { isLoading, isSuccess, isError, error: signUpError, data: signUpData },
  ] = useSignUpMutation();

  const [createBusiness] = useCreateBusinessMutation();

  const { data: industryTypesData } = useGetIndustryTypesQuery(null, {});
  const { data: influencerTypesData } = useGetInfluencerTypeQuery(null, {});
  const { data: regionsData } = useGetRegionsQuery(null, {});

  const length = formData.role_id === 3 ? 5 : 6;
  const steps = Array.from({ length }, (_, i) => i);

  useEffect(() => {
    const getIndustries = async () => {
      try {
        const categories = await fetchCategory();
        setBusinessTypesData(categories);
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    };
    getIndustries();
  }, []);

  useEffect(() => {
    if (isSuccess && signUpData?.data?.saveUser) {
      dispatch(setUser(signUpData?.data?.saveUser));
      setTimeout(() => {
        navigate("/home");
      }, 3000);
    }
  }, [isSuccess]);

  useEffect(() => {
    const getCustomerType = async () => {
      authServices.getCustomerType().then((response) => {
        const customerTypes: UserCustomerType[] = response.data;
        setCustomerTypeList(customerTypes);
      });
    };
    getCustomerType();
  }, []);

  useEffect(() => {
    if (!selectedIndustry) return setFilteredCategories(businessTypesData);

    if (industryTypesData && businessTypesData) {
      let newCategories = businessTypesData?.filter(
        (category: Category) =>
          category?.category !== "All" &&
          (category?.category_type === selectedIndustry ||
            category?.category_type === 0)
      );
      setFilteredCategories(newCategories);
    }
  }, [businessTypesData, selectedIndustry]);

  const handleNext = () => {
    if (activeStep == 0 && validate()) {
      authServices
        .checkEmail({ email: formData.email })
        .then(() => {
          dispatch(setActiveStep(activeStep + 1));
        })
        .catch((error) => {
          if (error.response.status === 409) {
            setEmailError("Email already in use");
            setTimeout(() => {
              setEmailError("");
            }, 3000);
          }
        });
    } else if (validate()) {
      dispatch(setActiveStep(activeStep + 1));
    }
    else {
      console.log("validation failed");
    }
  };

  const validate = () => {
    const errors: Record<string, string> = {};

    if (formData?.email?.length && !validateEmail(formData.email)) {
      errors.email = "Invalid Email Address";
    }
    if (formData?.password?.length && !validatePassword(formData.password)) {
      errors.password = "Password must be between 6 and 20 characters";
    }
    if (formData?.first_name?.length && !validateName(formData.first_name)) {
      errors.first_name = "Please enter a valid first name";
    }
    if (formData?.last_name?.length && !validateName(formData.last_name)) {
      errors.last_name = "Please enter a valid last name";
    }

    console.log(errors);
    setErrorMessage(errors);

    return Object.keys(errors).length === 0;
  };

  const handleBack = () => {
    setErrorMessage({});

    dispatch(setActiveStep(activeStep - 1));
  };
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, "");

    console.log(digits)
    const match = digits.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return value;

    let formatted = "";
    if (match[1]) formatted = `(${match[1]}`;
    if (match[1] && match[1].length === 3) formatted += ")";
    if (match[2]) formatted += ` ${match[2]}`;
    if (match[2] && match[2].length === 3) formatted += "-";
    if (match[3]) formatted += match[3];
    console.log(formatted)
    return formatted;
  };
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (errorMessage[name]) {
      const newEM = { ...errorMessage };
      delete newEM[name];
      setErrorMessage(newEM);
    }

    if (name === "phone") {
      setFormData({
        ...formData,
        [name]: formatPhoneNumber(value),
      });
      return;
    }

    // Character limit check for business_headline
    if (name === "business_headline") {
      if (value.length > 85) {
        setErrorMessage({
          ...errorMessage,
          business_headline: "Headline cannot exceed 85 characters.",
        });
        return; // Do not update state beyond 85 chars
      }
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelect = (e: SelectChangeEvent) => {
    setFormData({
      ...formData,
      [e.target.name]: parseInt(e.target.value) || e.target.value,
    });
  };
  const handleStateSelect = (_: any, newValue: string | null) => {
    console.log(newValue)
    console.log(_)
  };
  const handleSelectRole = (role_id: RoleId) => {
    const newFormData: SignUpFormState = { ...formData, role_id };
    console.log(role_id)
    // if (role_id === formData.role_id) return;

    if (role_id === 3) {
      newFormData.business = "";
      newFormData.phone = "";
      newFormData.address1 = "";
      newFormData.zipcode = "";
      newFormData.city = "";
      newFormData.state = "";
      newFormData.address2 = "";
      newFormData.description = "";
      newFormData.industry_id = 1;
      newFormData.business_type_id = [];
      newFormData.role_id = 3;
    } else if (role_id === 4) {
      console.log('test')
      newFormData.influencer_type = "";
      newFormData.companyName = "";
      newFormData.job_type = "";
      newFormData.avatar = "";
      newFormData.role_id = 4;
    }
    setFormData(newFormData);
  };

  const handleSelectIndustryCategory = (industry_ids: number) => {
    const businessTypes = formData.industry_ids || [];
    setSelectedIndustry(industry_ids);

    if (!businessTypes.includes(industry_ids)) {
      setFormData({
        ...formData,
        industry_ids: [...businessTypes, industry_ids],
      });
    }
  };

  const handleSelectBusinessType = (business_type_id: number) => {
    console.log(business_type_id)
    const businessTypes = formData.business_type_id || [];

    if (businessTypes.includes(business_type_id)) {
      setFormData({
        ...formData,
        business_type_id: businessTypes.filter(
          (item) => item !== business_type_id
        ),
      });
    } else {
      if (businessTypes.length < 3) {
        setFormData({
          ...formData,
          business_type_id: [...businessTypes, business_type_id],
        });
      }
    }
  };

  const handleSelectCustomerType = (customer_type_id: number) => {
    const businessTypes = formData?.customer_type_ids || [];

    if (businessTypes?.includes(customer_type_id)) {
      setFormData({
        ...formData,
        customer_type_ids: businessTypes.filter(
          (item) => item !== customer_type_id
        ),
      });
    } else {
      if (businessTypes.length < 3) {
        setFormData({
          ...formData,
          customer_type_ids: [...businessTypes, customer_type_id],
        });
      }
    }
  };

  const handleSelectCustomerCategories = (category_id: number) => {
    const businessTypes = formData.categories_ids || [];

    if (businessTypes.includes(category_id)) {
      setFormData({
        ...formData,
        categories_ids: businessTypes.filter((item) => item !== category_id),
      });
    } else {
      if (businessTypes.length < 3) {
        setFormData({
          ...formData,
          categories_ids: [...businessTypes, category_id],
        });
      }
    }
  };

  // MARK: Handle Submit
  const handleSubmit = async () => {
    let payload: CreateUserDto = {
      auth_type: 1,
      email: formData.email.toLowerCase(),
      first_name: formData?.first_name?.trim(),
      last_name: formData?.last_name?.trim(),
      password: formData.password,
      region_id: 1,
      role_id: formData.role_id,
      accepted_terms_and_conditions: false,
      influencer_type_id: parseInt(formData.influencer_type),
      work_place: formData.companyName ?? "",
      about_me: formData.about_me ?? "",
      job_type: formData.job_type ?? "",
      avatar: formData.avatar,
      customer_type_ids: formData?.customer_type_ids,
      categories_ids: formData?.categories_ids,
    };

    setLoader(true);

    if (activeStep === 4 && formData.role_id === 3) {
      // user account
      try {
        const response = await authServices.createUser(payload);
        const token = response?.data?.token;
        const user: User = response.data;

        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user) as string);

        dispatch(setUser(user));
        dispatch(resetAuthState());
        return navigate("/home");
      } catch (error) {
        console.log("error creating user: ", error);
      } finally {
        setLoader(false);
      }
    }

    if (activeStep === 5 && formData.role_id === 4) {
      try {
        const response = await authServices.createUser(payload);
        const token = response?.data?.token;
        const user: User = response.data;

        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user) as string);

        if (formData?.role_id == 4 && user?.id) {
          let businessPayload: CreateBusiness = {
            user_id: user?.id,
            business: formData.business,
            business_headline: formData?.business_headline,
            business_description: formData?.description,
            address1: formData.address1,
            address2: formData.address2,
            city: formData?.city,
            state: formData.state,
            zipcode: formData.zipcode,
            phone: formData.phone,
            industry_ids: formData.industry_ids,
            category_ids: formData.business_type_id,
            region_id: 1,
            timezone: "",
          };
          const result = await createBusiness(businessPayload).unwrap();
        }

        dispatch(setUser(user));
        dispatch(resetAuthState());
        navigate("/home");
      } catch (error: any) {
        console.error("error: ", error);
      } finally {
        setLoader(false);
      }
    }
  };

  // MARK: - Render functions

  const renderEnterDetails = () => {
    const handlePasswordToggle = () => {
      setShowPassword((prev) => !prev);
    };
    return (
      <Box display="flex" gap={2} flexDirection="column">
        <Typography variant="h1">Enter Details</Typography>
        <TextField
          label="Email Address"
          name="email"
          value={formData.email.toLowerCase()}
          onChange={handleChange}
          error={!!errorMessage?.email}
          helperText={errorMessage?.email}
        />
        <Box display="flex" alignItems="center">
          <TextField
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleChange}
            error={!!errorMessage?.password}
            helperText={errorMessage?.password}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handlePasswordToggle} edge="end">
                    {showPassword ? (
                      <img src={EyeClose} />
                    ) : (
                      <img src={EyeIcon} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {emailError && (
          <Typography
            sx={{
              textAlign: "center",
              color: "red",
              fontSize: "15px",
            }}
          >
            {emailError}
          </Typography>
        )}
        <Button
          disabled={
            !formData.email.length ||
            !formData.password.length ||
            Object.keys(errorMessage).length > 0
          }
          onClick={handleNext}
          variant="contained"
        >
          Next
        </Button>
      </Box>
    );
  };

  const renderEnterName = () => (
    <Box display="flex" gap={2} flexDirection="column">
      <Typography variant="h1">Enter Name</Typography>
      <TextField
        label="First Name"
        name="first_name"
        value={formData?.first_name}
        onChange={handleChange}
        error={!!errorMessage?.first_name}
        helperText={errorMessage?.first_name}
      />
      <TextField
        label="Last Name"
        name="last_name"
        value={formData?.last_name}
        onChange={handleChange}
        error={!!errorMessage?.last_name}
        helperText={errorMessage?.last_name}
      />
      {/* <FormControl fullWidth>
        <InputLabel id="region-label" htmlFor="region">
          Region
        </InputLabel>
        <Select
          label="Region"
          variant="outlined"
          name="region_id"
          value={formData.region_id + ""}
          onChange={handleSelect}
        >
          {regionsData?.map((region: Region) => (
            <MenuItem key={region.id} value={region.id}>
              {region.region}
            </MenuItem>
          ))}
        </Select>
      </FormControl> */}
      <Button
        onClick={handleNext}
        variant="contained"
        disabled={
          !formData.first_name?.length ||
          !formData.last_name?.length ||
          !formData.region_id ||
          Object.keys(errorMessage).length > 0
        }
      >
        Next
      </Button>
    </Box>
  );

  const renderUploadProfilePicture = () => (
    <Box display="flex" gap={2} flexDirection="column" alignItems="center">
      <Typography variant="h1" textAlign={"left"}>
        Upload Profile Picture
      </Typography>
      <Typography sx={{ width: "100%" }}>
        Please upload a clear face photo
      </Typography>
      <ImageUpload
        defaultImage={
          formData.avatar ? `${awsImageRootUrl}/${formData.avatar}` : null
        }
        onSelectImage={(image: string) => {
          setFormData({
            ...formData,
            avatar: image,
          });
        }}
      />
      <Button
        onClick={handleNext}
        variant="contained"
        fullWidth
        disabled={!formData.avatar}
      >
        Next
      </Button>
    </Box>
  );

  const renderSelectUserType = () => (
    <Box display="flex" gap={2} flexDirection="column">
      <Typography variant="h1">Select User Type</Typography>
      {roles.map((item, index) => (
        <FancyRadioButton
          key={index}
          icon={
            item.id === 3 ? <AccountCircleOutlined /> : <StorefrontOutlined />
          }
          title={item.name}
          description={item.desc}
          selected={formData.role_id === item.id}
          onClick={() => handleSelectRole(item.id)}
        />
      ))}

      <Button onClick={handleNext} variant="contained">
        Next
      </Button>
    </Box>
  );

  const renderEnterEmploymentDetails = () => (
    <Box display="flex" gap={2} flexDirection="column">
      <Typography variant="h1">Enter Employment Details</Typography>
      <FormControl fullWidth>
        <InputLabel id="job-type-label" htmlFor="influencer_type">
          Job Type
        </InputLabel>
        <Select
          label="Job Type"
          variant="outlined"
          name="influencer_type"
          value={formData.influencer_type}
          onChange={handleSelect}
        >
          {influencerTypesData?.map((influencerType: InfluencerType) => (
            <MenuItem key={influencerType.id} value={influencerType.id}>
              {influencerType.type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {Number(formData?.influencer_type) === 10 && (
        <TextField
          label="Specify Job Type"
          name="job_type"
          value={formData.job_type}
          onChange={handleChange}
          error={!!errorMessage?.job_type}
          helperText={errorMessage?.job_type}
        />
      )}

      <TextField
        label="Where do you work?"
        name="companyName"
        value={formData.companyName}
        onChange={handleChange}
        error={!!errorMessage?.companyName}
        helperText={errorMessage?.companyName}
        required
      />

      {/* customerTypesList */}
      <Box>
        <Typography variant="h5">
          Select your top 3 most common customer type: *
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {customerTypesList &&
            customerTypesList?.map((customer: UserCustomerType) => {
              const _selected = formData?.customer_type_ids?.includes(
                customer?.id
              );
              return (
                <Chip
                  key={customer.id}
                  label={customer.type}
                  onClick={() => handleSelectCustomerType(customer.id)}
                  variant={_selected ? "filled" : "outlined"}
                  color={_selected ? "primary" : "default"}
                  sx={{
                    margin: "0 0.5rem 0.5rem 0",
                    border: _selected ? "none" : "1px solid #000",
                  }}
                />
              );
            })}
        </Box>
      </Box>

      {/* Category List */}
      <Box>
        <Typography variant="h5">
          What are you most interesting on recommending? *
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {businessTypesData &&
            businessTypesData
              ?.filter((b) => b.category_type === 1)
              ?.map((category: Category) => {
                const _selected = formData?.categories_ids?.includes(
                  category?.id
                );

                return (
                  <Chip
                    key={category.id}
                    label={category.category}
                    onClick={() => handleSelectCustomerCategories(category.id)}
                    variant={_selected ? "filled" : "outlined"}
                    color={_selected ? "primary" : "default"}
                    sx={{
                      margin: "0 0.5rem 0.5rem 0",
                      border: _selected ? "none" : "1px solid #000",
                    }}
                  />
                );
              })}
        </Box>
      </Box>

      <Button
        onClick={handleSubmit}
        variant="contained"
        disabled={
          !formData.influencer_type ||
          !formData.companyName?.trim() ||
          Object.keys(errorMessage).length > 0 ||
          (Number(formData.influencer_type) === 10 && !formData.job_type) ||
          !(
            formData.customer_type_ids.length >= 1 &&
            formData.customer_type_ids.length <= 3
          ) ||
          !(
            formData.categories_ids.length >= 1 &&
            formData.categories_ids.length <= 3
          )
        }
      >
        Next
      </Button>
    </Box>
  );

  const renderBusinessDetails = () => (
    <Box display="flex" gap={2} flexDirection="column">
      <Typography variant="h1">Enter Business Details</Typography>
      <TextField
        label="Business Name"
        name="business"
        fullWidth
        onChange={handleChange}
        error={!!errorMessage?.busines}
        value={formData.business}
      />
      <TextField
        label="Phone"
        name="phone"
        value={formData.phone}
        onChange={handleChange}
        error={!!errorMessage?.phone}
        helperText={errorMessage?.phone}
        inputProps={{ maxLength: 14 }} // limit to (XXX) XXX-XXXX
      />
      <TextField
        label="Address"
        name="address1"
        value={formData.address1}
        onChange={handleChange}
        error={!!errorMessage?.address1}
        helperText={errorMessage?.address1}
      />
      <TextField
        label="Zipcode"
        name="zipcode"
        value={formData.zipcode}
        onChange={handleChange}
        error={!!errorMessage?.zipcode}
        helperText={errorMessage?.zipcode}
      />
      <TextField
        label="City"
        name="city"
        value={formData.city}
        onChange={handleChange}
        error={!!errorMessage?.city}
        helperText={errorMessage?.city}
      />
      <FormControl>
        <InputLabel id="state-label" htmlFor="state">
          State
        </InputLabel>
        <Select
          label="State"
          name="state"
          value={formData.state}
          onChange={handleSelect}
          error={!!errorMessage?.state}
        >
          {states.map((state) => (
            <MenuItem key={state.abbreviation} value={state.name}>
              {state.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Website"
        name="address2"
        value={formData.address2}
        onChange={handleChange}
        error={!!errorMessage?.website}
        helperText={errorMessage?.website}
      />
      <Button
        onClick={handleNext}
        variant="contained"
        disabled={
          !formData.business ||
          !formData.phone ||
          !formData.zipcode ||
          !formData.city ||
          !formData.state ||
          !formData.address1 ||
          Object.keys(errorMessage).length > 0
        }
      >
        Next
      </Button>
    </Box>
  );

  const renderBusinessCategory = () => (
    <Box display="flex" gap={2} mb={2} flexDirection="column">
      <Typography variant="h1">Business Details (continue)</Typography>
      <Typography sx={{ width: "100%" }}>Upload Business Photo</Typography>
      <ImageUpload
        variant="box"
        defaultImage={
          formData.image ? `${awsImageRootUrl}/${formData.image}` : null
        }
        onSelectImage={(image) => {
          setFormData({
            ...formData,
            image: image,
          });
        }}
      />

      <TextField
        label="Business Headline"
        name="business_headline"
        value={formData.business_headline}
        onChange={handleChange}
        error={!!errorMessage?.business_headline}
        helperText={errorMessage?.business_headline || `Remaining characters: ${85 -formData.business_headline.length}`}
      />

      <TextField
        multiline
        minRows={2}
        name="description"
        label="Business Description"
        value={formData.description}
        onChange={handleChange}
        style={{
          minHeight: "100px",
          width: "100%",
          borderRadius: "5px",
          borderColor: "#ddd",
        }}
      />

      <Typography variant="h1">Select Business Industry</Typography>
      <Typography>Select an Industry</Typography>
      {/* <Box display="flex" flexWrap="wrap" gap={1}>
        {industryTypesData &&
          industryTypesData.map((industryType: Industry) => {
            const _selected = selectedIndustry === industryType.id;

            return (
              <Chip
                key={industryType.id}
                label={industryType.industry}
                onClick={() => handleSelectIndustryCategory(industryType.id)}
                variant={_selected ? "filled" : "outlined"}
                color={_selected ? "primary" : "default"}
                sx={{
                  margin: "0 0.5rem 0.5rem 0",
                  border: _selected ? "none" : "1px solid #000",
                }}
              />
            );
          })}
      </Box> */}
      <Box display="flex" flexWrap="wrap" gap={1}>
        {industryTypesData &&
          industryTypesData.map((industryType: Industry) => {
            const _selected = selectedIndustry === industryType.id;
            const displayName = industryType.industry === 'Event Panning'
              ? 'Event Planning'
              : industryType.industry;

            return (
              <Chip
                key={industryType.id}
                label={displayName}
                onClick={() => handleSelectIndustryCategory(industryType.id)}
                variant={_selected ? 'filled' : 'outlined'}
                color={_selected ? 'primary' : 'default'}
                sx={{
                  margin: '0 0.5rem 0.5rem 0',
                  border: _selected ? 'none' : '1px solid #000',
                }}
              />
            );
          })}
      </Box>

      <InputLabel id="businessType-label" htmlFor="businessType">
        <strong> Business Type *</strong>
      </InputLabel>
      <Typography>Choose up to 3 categories</Typography>
      <Box>
        {filteredCategories &&
          filteredCategories
            .filter((businessType: Category) => businessType.category_type !== 0) // Filter out category_type 0
            .map((businessType: Category) => {
              const _selected = formData.business_type_id?.includes(
                businessType.id
              );
              return (
                <Chip
                  key={businessType.id}
                  label={businessType.category}
                  onClick={() => {
                    handleSelectBusinessType(businessType.id);
                  }}
                  variant={_selected ? "filled" : "outlined"}
                  color={_selected ? "primary" : "default"}
                  sx={{
                    margin: "0 0.5rem 0.5rem 0",
                    border: _selected ? "none" : "1px solid #000",
                  }}
                />
              );
            })}
      </Box>
      {/* <Box>
        {filteredCategories &&
          filteredCategories.map((businessType: Category) => {
            const _selected = formData.business_type_id?.includes(
              businessType.id
            );
            return (
              <Chip
                key={businessType.id}
                label={businessType.category}
                onClick={() => {
                  handleSelectBusinessType(businessType.id);
                }}
                variant={_selected ? "filled" : "outlined"}
                color={_selected ? "primary" : "default"}
                sx={{
                  margin: "0 0.5rem 0.5rem 0",
                  border: _selected ? "none" : "1px solid #000",
                }}
              />
            );
          })}
      </Box> */}
      <Button
        onClick={handleSubmit}
        variant="contained"
        disabled={
          loader ||
          formData.business_type_id?.length === 0 ||
          formData.industry_ids?.length === 0 ||
          !formData.image?.length ||
          !formData.business_headline?.length ||
          !formData.description?.length ||
          Object.keys(errorMessage).length > 0
        }
      >
        Continue
      </Button>
    </Box>
  );

  const renderLoadingModal = () => (
    <Modal
      open={isLoading || isSuccess}
      style={{ display: "flex", alignItems: "center" }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          border: "2px solid #000",
          boxShadow: 24,
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {isLoading && <CircularProgress size={48} />}
        {isSuccess && (
          <CheckCircleOutline color="success" sx={{ fontSize: "48px" }} />
        )}

        {isLoading && (
          <Typography id="modal-modal-title">
            Creating your profile...
          </Typography>
        )}
        {isSuccess && (
          <Typography id="modal-modal-title" color="success">
            Success! Your profile has been created. Redirecting you to your home
            page...
          </Typography>
        )}
      </Box>
    </Modal>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderEnterDetails();
      case 1:
        return renderEnterName();
      case 2:
        return renderUploadProfilePicture();
      case 3:
        return renderSelectUserType();
      case 4:
        if (formData.role_id === 3) {
          return renderEnterEmploymentDetails()
        }
        else {
          return renderBusinessDetails()
        }
      case 5:
        return renderBusinessCategory();
      default:
        return "There appears to be a problem, please reload the page.";
    }
  };
  // MARK: Render
  return (
    <Box display="flex" flexDirection="column" alignItems="center" width="100%">
      <Box maxWidth={"680px"} width="100%">
        <Box sx={{ width: "100%", mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={(activeStep / (steps.length - 1)) * 100}
            color="inherit"
            sx={{
              height: "12px",
              borderRadius: "5px",
              marginBottom: "2rem",
              marginX: 2,
            }}
          />
        </Box>
        <Button
          size="medium"
          onClick={handleBack}
          disabled={activeStep === 0}
          color="inherit"
        >
          <KeyboardArrowLeft />
          Back
        </Button>

        <Box sx={{ mt: 2, marginX: 2 }}>{renderStepContent(activeStep)}</Box>

        {isError && isFetchBaseQueryError(signUpError) && (
          <Typography variant="body1" color="error">
            {(signUpError.data as { message: string }).message}
          </Typography>
        )}
      </Box>
      {renderLoadingModal()}
    </Box>
  );
};

export default SignUpForm;
